import Guard from 'arg-guard'

import Service from './service'
import ServiceOperation from './service-operation'
import Logger from '@/logger'

/**
 * Gateway for accessing Results data.
 *
 * @export SavingService
 * @class SavingService
 */
export default class SavingService extends Service {

    /**
     * Creates an instance of SavingService.
     * @param {?number} schemeId
     * @memberof SavingService
     */
    constructor({ schemeId } = { schemeId: null }) {
        super('/savings', 'Results')

        Guard.throwIf(schemeId, 'schemeId')
        this.schemeId = schemeId
    }

    /**
     * Check the status of base metrics data for an address ID.
     *
     * @param {number} addressId
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof SavingService
     */
    async checkBaseStatisticsAsync(addressId) {
        Guard.throwIf(addressId, 'addressId')

        const operationState = new ServiceOperation('Base Statistics Status Fetcher', true)
        if (addressId === 0)
            return this._failOperation(new Error('No address ID specified (0)'), operationState, 'checkBaseStatisticsAsync')

        const url = `status/for-address/${ addressId }/in-scheme/${ this.schemeId }`
        Logger.log(`${ this.baseUrl }/${ url }`, 'Request [GET]')

        try {
            return this._processData(await this.axios.get(url, this._buildAxiosConfig()), operationState, 'checkBaseStatisticsAsync')
        } catch (err) {
            return this._failOperation(err, operationState, 'checkBaseStatisticsAsync')
        }
    }

    /**
     * Load base metrics data from an address ID.
     *
     * @param {number} addressId
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof SavingService
     */
    async loadBaseStatisticsAsync(addressId) {
        Guard.throwIf(addressId, 'addressId')

        const operationState = new ServiceOperation('Base Statistics Data Load', true)
        if (addressId === 0)
            return this._failOperation(new Error('No address ID specified (0)'), operationState, 'loadBaseStatisticsAsync')

        const url = `for-address/${ addressId }/in-scheme/${ this.schemeId }`
        Logger.log(`${ this.baseUrl }/${ url }`, 'Request [GET]')

        try {
            return this._processData(await this.axios.get(url, this._buildAxiosConfig()), operationState, 'loadBaseStatisticsAsync')
        } catch (err) {
            return this._failOperation(err, operationState, 'loadBaseStatisticsAsync')
        }
    }

    /**
     * Load an initial set of precalculated savings from an address ID.
     *
     * @param {number} addressId
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof SavingService
     */
    async loadInitialSavingsAsync(addressId) {
        Guard.throwIf(addressId, 'addressId')

        const operationState = new ServiceOperation('Precalculated Savings Load', true)
        if (addressId === 0)
            return this._failOperation(new Error('No address ID specified (0)'), operationState, 'loadInitialSavingsAsync')

        const url = `for-address/${ addressId }/in-scheme/${ this.schemeId }/initial`
        Logger.log(`${ this.baseUrl }/${ url }`, 'Request [GET]')

        try {
            return this._processData(await this.axios.get(url, this._buildAxiosConfig()), operationState, 'loadInitialSavingsAsync')
        } catch (err) {
            return this._failOperation(err, operationState, 'loadInitialSavingsAsync')
        }
    }

    /**
     * Load savings from an address ID and improvement IDs.
     *
     * @param {number} addressId
     * @param {Array} improvementIds An array of improvements to return savings data for.
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof SavingService
     */
    async loadSavingsAsync(addressId, improvementIds) {
        Guard.throwIf(addressId, 'addressId')
        Guard.throwIf(improvementIds, 'improvementIds')

        if (!improvementIds.length)
            throw new TypeError('No improvement ID Sets provided to load for savings.')

        const operationState = new ServiceOperation('Improvements Savings Load', true)
        if (addressId === 0)
            return this._failOperation(new Error('No address ID specified (0)'), operationState, 'loadSavingsAsync')

        const url = `for-address/${ addressId }/in-scheme/${ this.schemeId }/${ improvementIds }`
        Logger.log(`${ this.baseUrl }/${ url }`, 'Request [GET]')

        try {
            return this._processData(await this.axios.get(url, this._buildAxiosConfig()), operationState, 'loadSavingsAsync')
        } catch (err) {
            return this._failOperation(err, operationState, 'loadSavingsAsync')
        }
    }
}
